import React, { useCallback, useState, useEffect } from 'react';
import Hls from 'hls.js';
import SkipIntroStyl from './SkipIntro.styl';

export type SkipIntroProps = {
  hls: Hls;
  skipIntro: number;
  code: string;
};

const SkipIntro = ({ hls, skipIntro, code }: SkipIntroProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleSkipIntro = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!hls || !hls.media) {
      return;
    }

    // Pula para o tempo especificado em skipIntro
    hls.media.currentTime = skipIntro;
    setIsVisible(false);
  }, [hls, skipIntro]);

  const checkTimeAndUpdateVisibility = useCallback(() => {
    if (!hls || !hls.media) {
      return;
    }

    const time = hls.media.currentTime;
    // console.log('Current time:', time, 'skipIntro:', skipIntro);

    // Mostra o botão apenas durante o período da introdução
    // Esconde o botão 10 segundos antes do tempo especificado em skipIntro
    const endTime = skipIntro - 10;
    const shouldBeVisible = time < endTime && time >= 3;

    // console.log('Should be visible:', shouldBeVisible, 'endTime:', endTime);
    setIsVisible(shouldBeVisible);
  }, [hls, skipIntro]);

  useEffect(() => {
    // console.log('skipIntroTest', skipIntro);

    if (!hls || !hls.media) {
      return;
    }

    // console.log('Setting up SkipIntro for skipIntro:', skipIntro);

    const video = hls.media;

    // Usar múltiplos eventos para garantir funcionamento
    video.addEventListener('timeupdate', checkTimeAndUpdateVisibility);
    video.addEventListener('play', checkTimeAndUpdateVisibility);
    video.addEventListener('playing', checkTimeAndUpdateVisibility);
    video.addEventListener('pause', checkTimeAndUpdateVisibility);
    video.addEventListener('seeked', checkTimeAndUpdateVisibility);
    video.addEventListener('loadedmetadata', checkTimeAndUpdateVisibility);

    // Polling mais frequente para melhor responsividade
    intervalRef.current = setInterval(checkTimeAndUpdateVisibility, 500);

    // Verificar imediatamente
    checkTimeAndUpdateVisibility();

    return () => {
      video.removeEventListener('timeupdate', checkTimeAndUpdateVisibility);
      video.removeEventListener('play', checkTimeAndUpdateVisibility);
      video.removeEventListener('playing', checkTimeAndUpdateVisibility);
      video.removeEventListener('pause', checkTimeAndUpdateVisibility);
      video.removeEventListener('seeked', checkTimeAndUpdateVisibility);
      video.removeEventListener('loadedmetadata', checkTimeAndUpdateVisibility);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hls, hls?.media, checkTimeAndUpdateVisibility, skipIntro]);

  // console.log('isVisible', isVisible);

  if (!isVisible) {
    return null;
  }

  return (
    <SkipIntroStyl>
      <div className="smartplayer__skipIntro">
        <button
          className="smartplayer__skipIntroButton"
          type="button"
          onClick={handleSkipIntro}
          title="Pular introdução"
        >
          <span className="smartplayer__skipIntroText">
            Pular introdução
          </span>
          <div className="smartplayer__skipIntroIcon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7 6L17 12L7 18V6Z"
                fill="currentColor"
              />
              <path
                d="M17 6H19V18H17V6Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </button>
      </div>
    </SkipIntroStyl>
  );
};

export default SkipIntro;
